import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trip } from '@/types';
// Native MUI Components
import { Dialog, DialogTitle, DialogContent, Container, Typography, Box } from '@mui/material';
import { Add as PlusCircleIcon } from '@mui/icons-material';
import * as tripService from '@/services/tripService';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';
import Header from '@/components/layout/Header';
import AddTripForm from '@/components/trip/AddTripForm';
import DuplicateTripForm from '@/components/trip/DuplicateTripForm';
import TripSection from '@/components/trip/TripSection';
import PageSection from '@/components/ui/PageSection';
import { groupTripsByDate } from '@/components/trip/utils';

const AdminDashboardPage = () => {
    const [trips, setTrips] = useState<Trip[]>([]);
    const [duplicatingTrip, setDuplicatingTrip] = useState<Trip | null>(null);
    const [showAddTrip, setShowAddTrip] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();
    const { t } = useI18n();

    useEffect(() => {
        // Listen to all trips, without filters
        const unsubscribe = tripService.listenToTrips((updatedTrips) => {
            setTrips(updatedTrips);
        });

        return () => unsubscribe();
    }, []);

    const handleDuplicateTrip = (trip: Trip) => {
        setDuplicatingTrip(trip);
    };

    const handleToggleSignUp = async (trip: Trip) => {
        try {
            await tripService.updateTrip({ ...trip, isSignUpOpen: !trip.isSignUpOpen });
            toast({ 
                title: t('pages.adminDashboard.signUpStatusChanged'), 
                description: t('pages.adminDashboard.signUpStatusDescription', { 
                    tripName: trip.name, 
                    status: !trip.isSignUpOpen ? t('pages.adminDashboard.open') : t('pages.adminDashboard.closed') 
                }) 
            });
        } catch (error) {
            console.error("Fehler beim Ändern des Anmeldestatus:", error);
            toast({ 
                title: t('pages.adminDashboard.error'), 
                description: t('pages.adminDashboard.errorChangingSignUpStatus'), 
                variant: "destructive" 
            });
        }
    };

    const handleSelectTrip = (tripId: string) => {
        navigate(`/trip/${tripId}`);
    };

    const handleAddTripClick = () => {
        setShowAddTrip(true);
    };

    const handleBackToHome = () => {
        navigate('/');
    };

    // Gruppiere Reisen nach Datum
    const { current: currentTrips, past: pastTrips } = groupTripsByDate(trips);

    return (
        <Box sx={{ height: '100%' }}>
            <Header
                title={t('pages.adminDashboard.title')}
                onBack={handleBackToHome}
            />

            <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
                <PageSection
                    icon="🚀"
                    title={t('pages.adminDashboard.allTrips')}
                    subtitle={t('pages.adminDashboard.allTripsSubtitle')}
                    primaryAction={{
                        icon: <PlusCircleIcon />,
                        onClick: handleAddTripClick,
                        label: t('pages.adminDashboard.addTrip')
                    }}
                >
                    {/* Aktuelle Reisen Sektion */}
                    <TripSection
                        title={t('trip.currentTrips')}
                        trips={currentTrips}
                        emptyMessage={t('trip.noCurrentTrips')}
                        onSelectTrip={handleSelectTrip}
                        onDuplicateTrip={handleDuplicateTrip}
                        onToggleSignUp={handleToggleSignUp}
                    />

                    {/* Vergangene Reisen Sektion */}
                    <TripSection
                        title={t('trip.pastTrips')}
                        trips={pastTrips}
                        emptyMessage={t('trip.noPastTrips')}
                        onSelectTrip={handleSelectTrip}
                        onDuplicateTrip={handleDuplicateTrip}
                        onToggleSignUp={handleToggleSignUp}
                    />
                </PageSection>
            </Container>

            <AddTripForm open={showAddTrip} onOpenChange={setShowAddTrip} />

            <Dialog open={!!duplicatingTrip} onClose={() => setDuplicatingTrip(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box>
                        <Typography variant="h6" component="div">
                            {t('pages.adminDashboard.duplicateTrip')}
                        </Typography>
                        {duplicatingTrip && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t('pages.adminDashboard.duplicateTripDescription', { tripName: duplicatingTrip.name })}
                            </Typography>
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {duplicatingTrip && (
                        <DuplicateTripForm
                            trip={duplicatingTrip}
                            onTripDuplicated={(newTrip) => {
                                setDuplicatingTrip(null);
                                navigate(`/trip/${newTrip.id}`);
                            }}
                            onCancel={() => setDuplicatingTrip(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default AdminDashboardPage;
